import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Palette, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg } from "./api";

export const TYPES = ["Glass", "ACP", "HPL", "Stone", "Terracotta", "Aluminium Louvre", "Masonry / Plaster", "Concrete", "Other", "Exclude"];
export const TYPE_COLOR = { Glass: "#38a0e6", ACP: "#d6e0f0", HPL: "#b07848", Stone: "#ccba96", Terracotta: "#c4623c", "Aluminium Louvre": "#788291", "Masonry / Plaster": "#dcd2c0", Concrete: "#80807c", Other: "#96969e", Exclude: "#782428" };
const slug = (s) => s.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();

const Row = ({ x, value, onChange }) => (
  <tr className="border-t border-zinc-800/70" data-testid={`matmap-row-${slug(x.name)}`}>
    <td className="p-2"><span className="inline-block h-4 w-4 rounded border border-zinc-700 align-middle" style={{ background: x.color || "repeating-linear-gradient(45deg,#3f3f46 0 3px,#18181b 3px 6px)" }} title={x.color || "texture / no colour"} /></td>
    <td className="max-w-[220px] truncate p-2 font-mono text-xs text-zinc-200" title={x.name}>{x.name}</td>
    <td className="p-2 font-mono text-xs text-zinc-300">{x.area_m2} m²</td>
    <td className={`p-2 text-xs ${x.auto === "Glazed" ? "text-sky-300" : "text-zinc-500"}`}>{x.auto}</td>
    <td className="p-2">
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
        <SelectTrigger data-testid={`matmap-select-${slug(x.name)}`} className="h-8 w-52 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none" className="text-xs text-zinc-400">Unassigned (keep name)</SelectItem>
          {TYPES.map((t) => <SelectItem key={t} value={t} className="text-xs"><span className="mr-2 inline-block h-2 w-2 rounded-sm" style={{ background: TYPE_COLOR[t] }} />{t}</SelectItem>)}
        </SelectContent>
      </Select>
    </td>
  </tr>
);

export const MaterialMap = ({ project, setProject }) => {
  const found = project.model?.summary?.materials_found;
  const saved = project.material_map || {};
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(project.material_map || {}), [project.material_map]);
  if (!found) return <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300" data-testid="matmap-needs-reanalyse">Re-analyse the model once to list its SketchUp materials and assign cladding types.</div>;
  const dirty = found.some((x) => (draft[x.name] || null) !== (saved[x.name] || null));
  const save = async (mapping) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${project.id}/model/materials`, { mapping });
      setProject({ ...project, model: data.model, material_map: data.material_map });
      toast.success("Cladding types applied to the take-off");
      if (data.ignored?.length) toast.warning(`Ignored unknown material(s): ${data.ignored.join(", ")}`);
    } catch (e) { toast.error(errMsg(e, "Could not save the mapping")); } finally { setBusy(false); }
  };
  const reset = () => save(Object.fromEntries(found.map((x) => [x.name, null])));
  return (
    <div className="space-y-3 rounded-lg border border-zinc-800 bg-[#121215] p-4" data-testid="material-map-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-[Manrope] text-lg font-bold"><Palette className="h-4 w-4 text-sky-400" />Material mapping</h3>
          <p className="text-xs text-zinc-500">Assign each SketchUp material to a cladding type. Bands, totals and reports regroup by type; "Exclude" removes it from facade quantities. Re-analyse to also refresh skylight detection.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="matmap-reset-btn" variant="outline" size="sm" disabled={busy || !Object.keys(saved).length} onClick={reset} className="border-zinc-700"><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Clear</Button>
          <Button data-testid="matmap-apply-btn" size="sm" disabled={busy || !dirty} onClick={() => save(Object.fromEntries(found.map((x) => [x.name, draft[x.name] || null])))} className="bg-sky-500 text-white hover:bg-sky-600">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Apply mapping
          </Button>
        </div>
      </div>
      <div className="max-h-80 overflow-auto rounded-md border border-zinc-800">
        <table className="w-full min-w-[620px] text-sm" data-testid="matmap-table">
          <thead className="sticky top-0 bg-[#18181b] text-left font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            <tr><th className="p-2"></th><th className="p-2">SketchUp material</th><th className="p-2">Facade area</th><th className="p-2">Auto</th><th className="p-2">Cladding type</th></tr>
          </thead>
          <tbody>{found.map((x) => <Row key={x.name} x={x} value={draft[x.name]} onChange={(v) => setDraft((d) => ({ ...d, [x.name]: v }))} />)}</tbody>
        </table>
      </div>
    </div>
  );
};
